/**
 * Generic policy page renderer. Reads from /app/frontend/src/data/policies.js
 * keyed by URL slug. Uses editorial typography consistent with the rest of Social Pulse.
 */
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { POLICIES, POLICY_LINKS } from "@/data/policies";
import { ArrowLeft, FileText } from "lucide-react";

function Bullets({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-3 space-y-1.5 text-sm leading-relaxed pl-5 list-disc">
      {items.map((b, i) => <li key={i}>{b}</li>)}
    </ul>
  );
}

function Section({ s }) {
  return (
    <section className="mt-10" data-testid={`policy-section-${s.n}`}>
      <h2 className="font-display text-2xl md:text-3xl leading-snug">
        <span className="opacity-40 mr-3 font-mono text-lg">{s.n}.</span>
        {s.title}
      </h2>
      {s.body && (
        <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "var(--wofy-text)" }}>
          {s.body}
        </p>
      )}
      <Bullets items={s.bullets} />
      {s.after_bullets && (
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--wofy-text)" }}>
          {s.after_bullets}
        </p>
      )}
      <Bullets items={s.bullets_2} />
      {s.subgroups?.map((g, i) => (
        <div key={i} className="mt-5">
          <h3 className="font-display text-lg">{g.subtitle}</h3>
          {g.body && <p className="mt-2 text-[15px] leading-relaxed">{g.body}</p>}
          <Bullets items={g.bullets} />
          {g.footer_note && (
            <p className="mt-2 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>{g.footer_note}</p>
          )}
        </div>
      ))}
      {s.footer_note && (
        <p className="mt-4 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>{s.footer_note}</p>
      )}
    </section>
  );
}

export default function PolicyPage() {
  const { slug } = useParams();
  const policy = POLICIES[slug];
  if (!policy) return <Navigate to="/policies/terms-of-use" replace />;

  return (
    <div data-testid={`policy-page-${policy.slug}`} className="max-w-[900px] mx-auto px-6 lg:px-10 py-14">
      <Helmet>
        <title>{`${policy.title} — Social Pulse`}</title>
        <meta name="description" content={`${policy.title} for Social Pulse — effective ${policy.effective}, updated ${policy.updated}.`} />
        <link rel="canonical" href={`https://socialpulse.com/policies/${policy.slug}`} />
      </Helmet>

      <div className="text-[11px] font-mono uppercase tracking-wider mb-4 opacity-60">
        <Link to="/" className="hover:opacity-100">Home</Link>
        <span className="mx-2">/</span>
        <span>Policies</span>
        <span className="mx-2">/</span>
        <span>{policy.title}</span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <div className="overline">POLICY</div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="policy-heading">
        {policy.heading}
      </h1>
      <div className="mt-4 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
        Effective {policy.effective} · Last updated {policy.updated}
      </div>

      {policy.intro && (
        <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--wofy-text)" }}>
          {policy.intro}
        </p>
      )}

      {policy.sections.map((s) => <Section key={s.n} s={s} />)}

      {policy.closing && (
        <p className="mt-14 text-base italic border-t pt-8" style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-border)" }}>
          {policy.closing}
        </p>
      )}

      {/* Cross-links to sibling policies */}
      <div className="mt-14 pt-8 border-t" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="overline mb-3">MORE POLICIES</div>
        <div className="flex flex-wrap gap-2">
          {POLICY_LINKS.filter((p) => p.slug !== policy.slug).map((p) => (
            <Link
              key={p.slug}
              to={`/policies/${p.slug}`}
              data-testid={`policy-crosslink-${p.slug}`}
              className="wofy-btn-ghost text-xs"
            >
              {p.label}
            </Link>
          ))}
        </div>
        <Link to="/" className="mt-8 inline-flex items-center gap-1.5 text-sm underline">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </div>
    </div>
  );
}
